import { useState, useEffect, useCallback } from "react";
import {
  Select,
  Form,
  Tooltip,
  Button,
  Flex,
  Typography,
  ConfigProvider,
} from "antd";
import { Option } from "antd/es/mentions";
import { InputNumber } from "syngenta-digital-cropwise-react-ui-kit";
import infoIcon from "../../../assets/images/info.svg";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";
import "./style.less";
import PlatFormAPIAxiosInstance from "../../utils/platFormAxios";
import { DropdownEmptyState } from "../../../shared/layout/DropdownEmptyState";
import { Loader } from "../../components/Loader";
import { restrictToTwoDecimals } from "../../../pages/create-protocol/utils";
// import { DeleteIcon } from "../DeleteIcon";

/**
 *
 * @returns
 */
const StandardFertilizerRate = () => {
  const { t } = useTranslation();
  const {
    ratesDosage,
    setRatesDosage,
    setIsStandardDataValid,
    trialProtocolData,
    setFertilizerProducts,
  } = useNewTrialState();
  const [products, setProducts] = useState([]);
  const fertilizersCount = ratesDosage.standard_fertilisers?.length ?? 0;

  const getFertilizersData = useCallback(async () => {
    try {
      const cropData = await PlatFormAPIAxiosInstance.get(
        `/catalog/orgs/${trialProtocolData.org_id}/fertilizers?country=${trialProtocolData.crop_details.country}&full=true&include_hidden=true&size=100`
      );
      const fetchedProducts = cropData.data.content;
      setProducts(fetchedProducts);
      // Store in context so other components can access nutrients data
      setFertilizerProducts(fetchedProducts);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [trialProtocolData, setFertilizerProducts]);

  /**
   * Add product_id for missing items when products or standard_fertilisers change
   */
  useEffect(() => {
    if (!products.length || !ratesDosage.standard_fertilisers?.length) return;

    let updated = false;
    const updatedFertilisers = ratesDosage.standard_fertilisers.map((item) => {
      if (
        item.fertiliser &&
        !item.product_id &&
        products.some((p) => p.name === item.fertiliser)
      ) {
        updated = true;
        return {
          ...item,
          product_id: products.find((p) => p.name === item.fertiliser)?.id,
        };
      }
      return item;
    });

    if (updated) {
      setRatesDosage((prevRatesDosage) => ({
        ...prevRatesDosage,
        standard_fertilisers: updatedFertilisers,
      }));
    }
  }, [products, ratesDosage.standard_fertilisers]);

  /**
   * Fetch fertilizers data when trialProtocolData changes and has an id
   */
  useEffect(() => {
    if (trialProtocolData.id) {
      getFertilizersData();
    }
  }, [trialProtocolData]);

  const updatePlotFertilizerAmount = useCallback(
    (updatedRatesAndDosage) => {
      const updatedRatesDosageMap = ratesDosage?.rates_and_dosages?.map(
        (ratesDosagesArray) => {
          return ratesDosagesArray.map((ratesDosagesItem) => {
            let amount = 0;

            if (ratesDosagesItem.fertiliser_rate) {
              // Always an array from every call site, and `reduce` with a seed
              // of 0 always returns a number, so there is nothing to coalesce.
              const total = updatedRatesAndDosage.reduce((prev, current) => {
                return prev + (current?.rate ?? 0);
              }, 0);

              amount = (ratesDosagesItem.fertiliser_rate / 100) * total;
            }
            return {
              ...ratesDosagesItem,
              fertiliser_amount: amount,
            };
          });
        }
      );

      setRatesDosage({
        ...ratesDosage,
        rates_and_dosages: updatedRatesDosageMap,
        standard_fertilisers: [...updatedRatesAndDosage],
      });
    },
    [ratesDosage, setRatesDosage]
  );

  /**
   * when fertilizer rate is changed, update the rate and recalculate the fertiliser_amount for each item in rates_and_dosages
   * @param {*} rate
   * @param {*} id
   */
  // `id` always comes from a row rendered out of `standard_fertilisers`, so the
  // lookup below always finds its item -- no existence guard needed.
  const handleStandardFertilizerRateChange = (rate, id) => {
    const updatedRatesAndDosage = ratesDosage.standard_fertilisers;
    const itemToUpdate = updatedRatesAndDosage.find((item) => item.id === id);
    itemToUpdate.rate = rate;

    updatePlotFertilizerAmount(updatedRatesAndDosage);
  };

  /**
   * when new fertilizer is selected, update the product_id and nutrients snapshot
   * @param {*} fertilizer
   * @param {*} id
   */
  // As above, `id` is always a rendered row's own id, so the lookup succeeds.
  const handleStandardFertilizerChange = (fertilizer, id) => {
    const updatedRatesAndDosage = ratesDosage.standard_fertilisers;
    const itemToUpdate = updatedRatesAndDosage.find((item) => item.id === id);
    itemToUpdate.fertiliser = fertilizer;
    const selectedProduct = products.find(
      (product) => product.name === fertilizer
    );
    itemToUpdate.product_id = selectedProduct?.id;

    // Store nutrients snapshot from the selected product
    // API returns nutrients with "name" field, we map it to "nutrient_type"
    if (
      selectedProduct?.nutrients &&
      Array.isArray(selectedProduct.nutrients)
    ) {
      itemToUpdate.nutrients = selectedProduct.nutrients.map((nutrient) => ({
        nutrient_type: nutrient.name, // API uses "name" field
        percentage: nutrient.percentage,
      }));
    } else {
      itemToUpdate.nutrients = [];
    }

    setRatesDosage({
      ...ratesDosage,
      standard_fertilisers: [...updatedRatesAndDosage],
    });
  };

  /**
   * Add product_id for missing items when products or standard_fertilisers change
   */
  const addFertilizer = () => {
    const fertilizer = {
      rate: 0,
      fertiliser: "",
      unit_of_rate: "kg/ha",
      id: Date.now(),
      nutrients: [], // Always initialize with empty array
    };

    const updatedRatesAndDosage = {
      ...ratesDosage,
      standard_fertilisers: [
        ...(ratesDosage?.standard_fertilisers ?? []),
        fertilizer,
      ],
    };

    setRatesDosage(updatedRatesAndDosage);
  };

  /**
   * Validate standard fertilisers data whenever ratesDosage changes
   */
  useEffect(() => {
    const validateStandardFertilisers = () => {
      const isValid =
        ratesDosage.standard_fertilisers?.length > 0 &&
        ratesDosage.standard_fertilisers.every(
          (item) => item.rate > 0 && item.fertiliser?.trim() !== ""
        );
      setIsStandardDataValid(isValid);
    };

    validateStandardFertilisers();
  }, [ratesDosage]);

  /**
   * update the rates_and_dosages and standard_fertilisers when a fertilizer is deleted
   * @param {*} id
   */
  const deleteFertilizer = (id) => {
    const updatedRatesAndDosage = ratesDosage.standard_fertilisers;
    const filteredRates = updatedRatesAndDosage.filter(
      (item) => item.id !== id
    );
    updatePlotFertilizerAmount(filteredRates);
  };

  return (
    <div className="standard-seedrate-main">
      <div className="seedrate-header">
        <p className="seedrate-heading">
          {formatTranslation(
            t("trials.plotDesign.trialPlots.table.fieldsStandardRate")
          )}
        </p>
        <Tooltip
          placement="right"
          title={formatTranslation(
            t("trials.plotDesign.StandardFertilizerRate.tooltip")
          )}
        >
          <img src={infoIcon} alt="info-icon" className="seedrate-info-icon" />
        </Tooltip>
      </div>
      {ratesDosage.standard_fertilisers?.map((item) => {
        return (
          <Form
            className="seedrate-selections"
            key={item.id}
            style={{ marginBottom: "8px" }}
          >
            <ConfigProvider renderEmpty={DropdownEmptyState}>
              <Flex vertical style={{ flex: "1" }}>
                <Form.Item
                  label={formatTranslation(t("newTrial.ratesDosage.fetiliser"))}
                  className="select-product"
                  style={{ marginBottom: "8px", width: "100%" }}
                >
                  <Select
                    placeholder={formatTranslation(
                      t("trials.plotDesign.trialPlots.select")
                    )}
                    value={item.fertiliser}
                    onChange={(product) => {
                      handleStandardFertilizerChange(product, item.id);
                    }}
                    className="selected-product"
                  >
                    {products.length > 0 ? (
                      products
                        .filter((product) => {
                          return !ratesDosage.standard_fertilisers.some(
                            (obj) =>
                              obj.fertiliser === product.name &&
                              obj.fertiliser !== item.fertiliser
                          );
                        })
                        .map((product) => {
                          return (
                            <Option
                              key={product.id ?? product.name}
                              value={product.name}
                            >
                              {product.name}
                            </Option>
                          );
                        })
                    ) : (
                      <Loader mask />
                    )}
                  </Select>
                </Form.Item>
                <div style={{ marginTop: "16px", color: "red" }}>
                  {!item.fertiliser && (
                    <span
                      className="error-message"
                      style={{ marginTop: "8px" }}
                    >
                      {formatTranslation(
                        t("trials.plotDesign.trialPlots.errorText.fertiliser")
                      )}
                    </span>
                  )}
                  {!item.rate && item.fertiliser && (
                    <span
                      className="error-message"
                      style={{ marginTop: "8px" }}
                    >
                      {formatTranslation(
                        t(
                          "trials.plotDesign.trialPlots.errorText.fertiliserRate"
                        )
                      )}
                    </span>
                  )}
                </div>
              </Flex>
            </ConfigProvider>
            <Form.Item
              label={formatTranslation(
                t("newTrial.ratesDosage.fertilizerRate.rateKgPerHa")
              )}
              className="input-seedrate"
            >
              <InputNumber
                placeholder="0"
                size="large"
                min={0}
                precision={2}
                step={0.01}
                value={item.rate ?? 0}
                onChange={(seedRate) => {
                  handleStandardFertilizerRateChange(seedRate, item.id);
                }}
                onKeyDown={(e) => restrictToTwoDecimals(e)}
                className="input-number"
              />
            </Form.Item>

            {fertilizersCount >= 2 && (
              <button
                type="button"
                className="delete-fertilizer-button"
                style={{ alignSelf: "flex-start", marginTop: 40 }}
                onClick={() => deleteFertilizer(item.id)}
                aria-label={formatTranslation(
                  t("newTrial.ratesDosage.fertilizerRate.deleteFertiliser")
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="18"
                  viewBox="0 0 14 18"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.5 0H4.5L3.5 1H0V3H14V1H10.5L9.5 0ZM11 6V16H3V6H11ZM1 4H13V16C13 17.1 12.1 18 11 18H3C1.9 18 1 17.1 1 16V4Z"
                    fill="#EB4B4B"
                  />
                </svg>
              </button>
            )}
          </Form>
        );
      })}
      <Flex justify="space-between">
        <Button type="text" onClick={addFertilizer}>
          {formatTranslation(t("trialtypes.label.moreNutrients"))}
        </Button>
        <Typography.Text>
          {formatTranslation(t("trials.plotDesign.trialPlots.totalProduct"))}{" "}
          {ratesDosage.standard_fertilisers?.reduce((prev, current) => {
            prev = prev + (current?.rate ?? 0);
            return prev;
          }, 0)}
          &nbsp;(kg/ha)
        </Typography.Text>
      </Flex>
    </div>
  );
};

export default StandardFertilizerRate;
